import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Inbox, RefreshCw, AlertTriangle, Clock, Plus, FileText } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { SummaryCard } from '@/components/dashboard/SummaryCard';
import { SLAChart } from '@/components/dashboard/SLAChart';
import { ActivityFeed } from '@/components/dashboard/ActivityFeed';
import { StatusBadge } from '@/components/custom/StatusBadge';
import { PriorityBadge } from '@/components/custom/PriorityBadge';
import { Skeleton } from '@/components/ui/skeleton';
import { fetchDashboardStats, fetchDashboardActivity, fetchTickets } from '@/hooks/useTickets';
import type { DashboardStatsResponse, DashboardActivityItem, TicketListItem } from '@uniticket/shared';

const cardIcons = [
  <div key="open" className="w-11 h-11 rounded-xl bg-red-50 flex items-center justify-center">
    <Inbox className="w-5 h-5 text-red-500" />
  </div>,
  <div key="progress" className="w-11 h-11 rounded-xl bg-orange-50 flex items-center justify-center">
    <RefreshCw className="w-5 h-5 text-orange-500" />
  </div>,
  <div key="createdByMe" className="w-11 h-11 rounded-xl bg-slate-100 flex items-center justify-center">
    <FileText className="w-5 h-5 text-brand-anthracite" />
  </div>,
  <div key="escalated" className="w-11 h-11 rounded-xl bg-purple-50 flex items-center justify-center">
    <AlertTriangle className="w-5 h-5 text-purple-600" />
  </div>,
  <div key="breached" className="w-11 h-11 rounded-xl bg-red-50 flex items-center justify-center">
    <Clock className="w-5 h-5 text-red-600" />
  </div>,
];

// SLA progress bar helper
function SLABar({ deadline, createdAt }: { deadline: string | null; createdAt: string }) {
  const { t } = useTranslation(['dashboard']);

  if (!deadline) {
    return (
      <div className="text-right ml-4 shrink-0">
        <p className="text-xs font-medium text-slate-400">{t('dashboard:slaBar.noSla')}</p>
      </div>
    );
  }

  const now = Date.now();
  const deadlineMs = new Date(deadline).getTime();
  const createdMs = new Date(createdAt).getTime();
  const totalDuration = deadlineMs - createdMs;
  const elapsed = now - createdMs;
  const remaining = deadlineMs - now;

  // Progress percentage (how much of the SLA window has elapsed)
  const progress = totalDuration > 0 ? Math.min((elapsed / totalDuration) * 100, 100) : 0;

  let label: string;
  let color: string;
  let barColor: string;

  if (remaining <= 0) {
    label = t('dashboard:slaBar.breached');
    color = 'text-red-500 font-bold';
    barColor = 'bg-red-500';
  } else if (progress >= 80) {
    // At risk (>= 80% of SLA time elapsed)
    const hoursLeft = Math.ceil(remaining / 3600_000);
    label = hoursLeft >= 24 ? t('dashboard:slaBar.daysLeft', { count: Math.ceil(remaining / (24 * 3600_000)) }) : t('dashboard:slaBar.hoursLeft', { count: hoursLeft });
    color = 'text-yellow-600';
    barColor = 'bg-yellow-500';
  } else {
    const hoursLeft = Math.ceil(remaining / 3600_000);
    label = hoursLeft >= 24 ? t('dashboard:slaBar.daysLeft', { count: Math.ceil(remaining / (24 * 3600_000)) }) : t('dashboard:slaBar.hoursLeft', { count: hoursLeft });
    color = 'text-green-600';
    barColor = 'bg-green-500';
  }

  return (
    <div className="text-right ml-4 shrink-0">
      <p className={`text-xs font-medium ${color}`}>{label}</p>
      <div className="w-20 h-1.5 bg-slate-100 rounded-full mt-1.5">
        <div
          className={`h-full rounded-full ${barColor}`}
          style={{ width: `${Math.min(progress, 100)}%` }}
        />
      </div>
    </div>
  );
}

export function Dashboard() {
  const { t } = useTranslation(['dashboard', 'common']);
  const { user, isAdmin, isExternal } = useAuth();
  const [stats, setStats] = useState<DashboardStatsResponse | null>(null);
  const [activities, setActivities] = useState<DashboardActivityItem[] | null>(null);
  const [recentTickets, setRecentTickets] = useState<TicketListItem[]>([]);
  const [recentTicketsTotal, setRecentTicketsTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const loadDashboard = useCallback(async () => {
    try {
      const params: Record<string, string> = { limit: '5', sort: 'createdAt', order: 'desc' };

      // Internal non-admin: show my assigned tickets
      if (!isAdmin && !isExternal && user) {
        params.assignedTo = user.id;
      }

      const [statsRes, activityRes, ticketsRes] = await Promise.all([
        fetchDashboardStats(),
        fetchDashboardActivity(),
        fetchTickets(params),
      ]);

      setStats(statsRes.data);
      setActivities(activityRes.data);
      setRecentTickets(ticketsRes.data);
      setRecentTicketsTotal(ticketsRes.meta?.total ?? ticketsRes.data.length);
    } catch {
      // Silently fail — components handle empty states
    } finally {
      setLoading(false);
    }
  }, [isAdmin, isExternal, user]);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);

  const firstName = user?.displayName.split(' ')[0] || 'User';

  // Build dynamic greeting
  const hour = new Date().getHours();
  const timeGreeting = hour < 12 ? t('dashboard:greeting.morning') : hour < 18 ? t('dashboard:greeting.afternoon') : t('dashboard:greeting.evening');

  // Build subtitle
  let subtitle: React.ReactNode = t('dashboard:subtitle.loading');
  if (stats) {
    const parts: React.ReactNode[] = [];
    if (stats.counts.slaBreached > 0) {
      parts.push(
        <span key="sla">
          {t('dashboard:subtitle.slaBreaches', { count: stats.counts.slaBreached })}
        </span>,
      );
    }
    if (!isExternal && stats.personal.assignedToMe > 0) {
      parts.push(
        <span key="assigned">
          {t('dashboard:subtitle.assignedToYou', { count: stats.personal.assignedToMe })}
        </span>,
      );
    }
    parts.push(
      <span key="total">
        {t('dashboard:subtitle.ticketsTotal', { count: stats.counts.total })}
      </span>,
    );
    subtitle = parts.reduce<React.ReactNode[]>((acc, part, i) => {
      if (i > 0) acc.push(<span key={`sep-${i}`}> &middot; </span>);
      acc.push(part);
      return acc;
    }, []);
  }

  // Cards from real data
  const cards = stats
    ? [
        { title: t('dashboard:cards.open'), value: stats.counts.open, subtitle: t('dashboard:cards.createdByYou', { count: stats.personal.createdByMe }), accent: 'rose' as const, linkTo: '/tickets?status=OPEN' },
        { title: t('dashboard:cards.myActive'), value: stats.counts.myActive, subtitle: t('dashboard:cards.assignedToYou', { count: stats.personal.assignedToMe }), accent: 'orange' as const, linkTo: '/tickets?status=OPEN,IN_PROGRESS&assignedTo=me' },
        { title: t('dashboard:cards.myCreated'), value: stats.personal.createdByMe, subtitle: t('dashboard:cards.myCreatedHint'), accent: 'anthracite' as const, linkTo: user ? `/tickets?createdBy=${user.id}` : '/tickets' },
        { title: t('dashboard:cards.escalated'), value: stats.counts.escalated, subtitle: stats.counts.escalated > 0 ? t('dashboard:cards.needsAttention') : t('dashboard:cards.allClear'), accent: 'purple' as const, linkTo: '/tickets?escalated=true' },
        { title: t('dashboard:cards.slaBreached'), value: stats.counts.slaBreached, subtitle: stats.counts.slaBreached > 0 ? t('dashboard:cards.actionRequired') : t('dashboard:cards.allOnTrack'), accent: 'red' as const, linkTo: '/tickets?slaBreached=true' },
      ]
    : [];

  // Ticket section title
  const ticketSection = isAdmin
    ? t('dashboard:recentTickets.allRecent')
    : isExternal
    ? t('dashboard:recentTickets.myTickets')
    : t('dashboard:recentTickets.myAssigned');

  return (
    <div className="space-y-8">
      {/* Welcome banner */}
      <div className="gradient-band rounded-xl p-6 text-white flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold">{timeGreeting}, {firstName}</h2>
          <p className="text-white/70 text-sm mt-1">{subtitle}</p>
        </div>
        <div className="hidden md:block">
          <img src="/UnifiberLogoWhite.png" alt="Unifiber" className="h-10 opacity-40" />
        </div>
      </div>

      {/* Summary cards */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5">
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} className="h-28 rounded-xl" />
          ))}
        </div>
      ) : (
        <div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5">
            {cards.map((card, i) => (
              <SummaryCard
                key={card.title}
                title={card.title}
                value={card.value}
                subtitle={card.subtitle}
                accentColor={card.accent}
                icon={cardIcons[i]}
                valueClassName={card.accent === 'red' && card.value > 0 ? 'text-red-600' : undefined}
                linkTo={card.linkTo}
              />
            ))}
          </div>
          <p className="text-xs text-slate-400 mt-2 text-center">{t('dashboard:cards.overlap')}</p>
        </div>
      )}

      {/* Two-column: Recent tickets + SLA */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Recent tickets (2/3) */}
        <div className="xl:col-span-2 bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="gradient-band px-6 py-3 flex items-center justify-between">
            <h3 className="text-sm font-bold text-white uppercase tracking-wide">
              {ticketSection}
              {!loading && recentTicketsTotal > 0 && (
                <span className="ml-2 text-xs font-normal text-white/60 normal-case">
                  {recentTickets.length < recentTicketsTotal
                    ? t('dashboard:recentTickets.ofTotal', { shown: recentTickets.length, total: recentTicketsTotal })
                    : `${recentTicketsTotal}`}
                </span>
              )}
            </h3>
            <Link to="/tickets" className="text-xs text-white/70 hover:text-white transition-colors font-medium">
              {t('dashboard:recentTickets.fullList')} &rarr;
            </Link>
          </div>

          {/* Ticket rows */}
          {loading ? (
            <div className="divide-y divide-slate-100">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="px-6 py-4">
                  <Skeleton className="h-4 w-3/4 mb-2" />
                  <Skeleton className="h-3 w-1/2" />
                </div>
              ))}
            </div>
          ) : recentTickets.length === 0 ? (
            <div className="px-6 py-12 text-center text-slate-400 text-sm">
              {t('dashboard:recentTickets.noTickets')} <Link to="/tickets/new" className="text-brand-rose hover:underline">{t('dashboard:recentTickets.createFirst')}</Link>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {recentTickets.map((ticket) => (
                <Link
                  key={ticket.id}
                  to={`/tickets/${ticket.id}`}
                  className="flex items-center px-6 py-4 hover:bg-slate-50 transition-colors group"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-bold text-brand-rose">{ticket.ticketNumber}</span>
                      <StatusBadge status={ticket.status} />
                      <PriorityBadge priority={ticket.priority} />
                    </div>
                    <p className="text-sm font-medium text-slate-800 mt-1 truncate group-hover:text-brand-rose transition-colors">
                      {ticket.subject}
                    </p>
                    <p className="text-xs text-slate-400 mt-1">
                      {(isAdmin || !isExternal) && <>{ticket.operator.name} &bull; </>}
                      {t(`common:ticketTypeShort.${ticket.ticketType}`)}
                      {isAdmin && ticket.assignee && (
                        <> &bull; {t('dashboard:recentTickets.assignedToName', { name: ticket.assignee.displayName })}</>
                      )}
                    </p>
                  </div>
                  <SLABar deadline={ticket.slaResolutionDeadline} createdAt={ticket.createdAt} />
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* SLA Overview (1/3) */}
        <SLAChart
          onTrack={stats?.sla.onTrack ?? 0}
          atRisk={stats?.sla.atRisk ?? 0}
          breached={stats?.sla.breached ?? 0}
          resolved={stats?.sla.resolved ?? 0}
          totalTickets={stats?.counts.total ?? 0}
          showOperatorBreakdown={!isExternal}
          operatorBreakdown={stats?.operatorBreakdown}
        />
      </div>

      {/* Recent Activity */}
      <ActivityFeed activities={activities ?? undefined} loading={loading} />

      {/* Quick action — floating new ticket button on mobile */}
      <Link
        to="/tickets/new"
        className="fixed bottom-6 right-6 md:hidden w-14 h-14 rounded-full gradient-band flex items-center justify-center shadow-lg shadow-brand-rose/30 z-30"
      >
        <Plus className="w-6 h-6 text-white" />
      </Link>
    </div>
  );
}
